// PracticeOfflinePage.tsx · v12.10.30 (#2255 practice offline - "Your account is taken offline" on every screen and the sign-in page) · 2026-11-03 · VoiceSpectrum AI
//
// v12.10.30 (#2255, Tushar 11/3): the third page beside "Looks like you're offline" and "We'll be right back" (MaintenanceOverlay) - the
// SAME purple offline theme, card and VoiceSpectrum AI lockup, plus an orange border round the card (UI-PROTO-2255 v1.1).
//   Headline (~5:30 PM): "your account is taken offline". Line: "<Practice> has been taken offline by VoiceSpectrum. Your data is safe."
//   ✓ / ✕ what still works / what does not - follows the call option (Voicemail only / Keep answering / Not answered), as the prototype's W.
//   "plugs male + female with an animation" - the two plugs gently pull apart and spark, loop; prefers-reduced-motion = still picture.
//   Check status (asks the server now; also by itself every 60 s) · Report issue · Contact support (the sign-in page's form). No
//   reconnect dots - it does not reconnect by itself; it closes the moment Support brings the practice back.
// Mounted once at the app root (App.tsx), so it covers every screen of the practice app AND the sign-in page (no sign-in needed). It shows
// when GET /api/public/practice-status says offline, or when any practice call answers 423 practice_offline. A VoiceSpectrum Support
// visit never sees it ("support visits yes").
import { useCallback, useEffect, useState } from 'react';
import './MaintenanceOverlay.css';
import './PracticeOfflinePage.css';
import '../styles/login.css';
import ReportIssue from './ReportIssue';
import { SupportSentView, SupportView, type SupportFields, type SupportOutcome } from '../pages/login/views';
import { Banner } from '../pages/login/parts';
import {
  currentUserContact, currentUserLabel, fetchPracticeStatus, flushReports, isSupportVisit, onPracticeOffline, postPublic, practiceSlug,
  timeText, type CallOption, type PracticeOfflineInfo,
} from '../lib/practiceOffline';

// v12.10.30 (#2255): what still works / what does not, per call option - the prototype's W table, with "Automatic texts" following the
// call option (Tushar 4:45 PM: "everthing stops or workign depending on what option is selected").
const W: Record<CallOption, { works: string[]; not: string[] }> = {
  voicemail: {
    works: ['Callers can leave a voicemail - you’ll see them in Messages when your account is back', 'Your data, settings and messages are kept'],
    not: ['The AI receptionist is not answering calls', 'Signing in to VoiceSpectrum', 'Automatic texts to patients'],
  },
  keep: {
    works: ['The AI receptionist keeps answering your calls', 'New messages are saved for when your account is back', 'Your data, settings and messages are kept'],
    not: ['Signing in to VoiceSpectrum'],
  },
  none: {
    works: ['Your data, settings and messages are kept'],
    not: ['Calls to your VoiceSpectrum number are not answered', 'Voicemail', 'Signing in to VoiceSpectrum', 'Automatic texts to patients'],
  },
};

/** v12.10.30 (#2255): "male + female plug picture with an animation" - the plugs pull apart and spark, loop. */
function PlugPicture() {
  return (
    <svg className="vs-po-plug" viewBox="0 0 160 80" role="img" aria-label="Unplugged">
      <g className="vs-po-plug-left">
        <path d="M2 50 C22 50 26 40 46 40" fill="none" stroke="#94a3b8" strokeWidth="5" strokeLinecap="round" />
        <rect x="44" y="28" width="24" height="24" rx="5" fill="#64748b" />
        <rect x="66" y="33" width="13" height="4" rx="1.5" fill="#475569" />
        <rect x="66" y="43" width="13" height="4" rx="1.5" fill="#475569" />
      </g>
      <g className="vs-po-plug-right">
        <rect x="90" y="26" width="26" height="28" rx="5" fill="#cbd5e1" />
        <rect x="90" y="33" width="6" height="4" rx="1" fill="#94a3b8" />
        <rect x="90" y="43" width="6" height="4" rx="1" fill="#94a3b8" />
        <path d="M116 40 C134 40 136 28 158 26" fill="none" stroke="#cbd5e1" strokeWidth="5" strokeLinecap="round" />
      </g>
      <g className="vs-po-spark" stroke="#ea580c" strokeWidth="3" strokeLinecap="round">
        <path d="M82 20 l1 -8" /><path d="M88 22 l5 -6" /><path d="M76 23 l-5 -5" />
        <path d="M82 60 l1 8" /><path d="M88 58 l5 6" /><path d="M76 57 l-5 5" />
      </g>
    </svg>
  );
}

type SupportState = { contact: string; reference: string; mode: SupportOutcome; sentAt?: string | null; count?: number; draft?: string };

export default function PracticeOfflinePage() {
  const [info, setInfo] = useState<PracticeOfflineInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<{ text: string; err?: boolean } | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [support, setSupport] = useState<SupportState | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const shown = info !== null;

  useEffect(() => {
    void flushReports();   // v12.10.30 (#2255): reports that waited (you're offline / we'll be right back) go now
    if (isSupportVisit()) return undefined;
    let cancelled = false;
    fetchPracticeStatus().then((s) => { if (!cancelled && s.offline) setInfo(s); }).catch(() => { /* can't tell - stay out of the way */ });
    const off = onPracticeOffline((b) => {
      if (isSupportVisit()) return;
      setInfo((prev) => ({ ...(prev ?? {}), ...b }));
      fetchPracticeStatus().then((s) => { if (s.offline) setInfo((prev) => ({ ...(prev ?? {}), ...s })); }).catch(() => { /* keep what the 423 said */ });
    });
    return () => { cancelled = true; off(); };
  }, []);

  /** Check status - "still offline -> 'Still offline as of 7:42 PM'; back -> the page closes and the app opens". */
  const check = useCallback(async (manual: boolean) => {
    if (manual) { setChecking(true); setCheckMsg(null); }
    try {
      const s = await fetchPracticeStatus();
      if (!s.offline) { setInfo(null); window.location.reload(); return; }
      setInfo((prev) => ({ ...(prev ?? {}), ...s }));
      setCheckMsg({ text: `Still offline as of ${timeText(new Date())}` });
    } catch {
      if (manual) setCheckMsg({ text: 'We couldn’t check just now - try again in a moment.', err: true });
    } finally {
      if (manual) setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!shown) return undefined;
    const t = window.setInterval(() => { void check(false); }, 60_000);   // re-checks by itself every 60 s
    return () => window.clearInterval(t);
  }, [shown, check]);

  if (!info) return null;

  const option: CallOption = info.callOption === 'keep' || info.callOption === 'none' ? info.callOption : 'voicemail';
  const w = W[option];
  const practice = info.practice || 'Your practice';
  const org = practice;

  // ── Contact support - the sign-in page's form (SupportView / SupportSentView), same API ──────────────────────────────
  type SupportAnswer = { status?: SupportOutcome; reference?: string; contact?: string; sentAt?: string | null; count?: number };
  async function sendSupport(f: SupportFields) {
    setBusy(true); setNotice(null);
    try {
      const r = await postPublic<SupportAnswer>('/api/public/support-request',
        { name: f.name || null, practice: f.practice || null, slug: practiceSlug(), contact: f.contact, message: f.message });
      if (r.status !== 200 || !r.data) throw new Error(String(r.status));
      setSupport({ contact: r.data.contact ?? f.contact, reference: r.data.reference ?? '', mode: r.data.status ?? 'created', sentAt: r.data.sentAt,
        count: r.data.count, draft: r.data.status === 'existing' ? f.message : undefined });
    } catch (e) {
      setNotice((e as Error)?.message === '429'
        ? 'Too many requests from this network. Try again in an hour, or email support@voicespectrum.ai.'
        : 'We couldn’t send that just now. Try again, or email support@voicespectrum.ai.');
    } finally { setBusy(false); }
  }
  async function addToSupport(text: string) {
    if (!support) return;
    setBusy(true); setNotice(null);
    try {
      const r = await postPublic<SupportAnswer>('/api/public/support-request/add', { reference: support.reference, contact: support.contact, message: text });
      if (r.status === 404) { setNotice('That request is no longer open. Go back and send a new one.'); return; }
      if (r.status !== 200) throw new Error(String(r.status));
      setSupport({ ...support, mode: r.data?.status === 'limited' ? 'limited' : 'added', count: r.data?.count ?? support.count, draft: undefined });
    } catch {
      setNotice('We couldn’t add that just now. Try again, or email support@voicespectrum.ai.');
    } finally { setBusy(false); }
  }
  const closeContact = () => { setContactOpen(false); setSupport(null); setNotice(null); };
  const noticeEl = notice ? <Banner kind="bad">{notice}</Banner> : undefined;

  return (
    <div className="vs-maint-overlay vs-po-overlay" role="alert" aria-live="assertive" data-testid="practice-offline-page">
      <div className="vs-maint-card vs-po-card">
        <img className="vs-maint-lockup vs-po-lockup" src="/brand/svg/19_compact_lockup_light.svg" alt="VoiceSpectrum AI"
          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        {contactOpen ? (
          <div className="tenant-login-panel vsl vs-po-contact">
            {support ? (
              <SupportSentView org={org} contact={support.contact} reference={support.reference} onBack={closeContact} backText="Back"
                mode={support.mode} sentAt={support.sentAt} count={support.count} draft={support.draft} busy={busy} notice={noticeEl}
                onAdd={(t) => void addToSupport(t)} />
            ) : (
              <SupportView org={org} notice={noticeEl} busy={busy} onSend={(f) => void sendSupport(f)} onBack={closeContact} backText="Back"
                initial={{ name: currentUserLabel() ?? '', practice: info.practice ?? '', contact: currentUserContact(), message: '' }} />
            )}
          </div>
        ) : (
          <>
            <PlugPicture />
            <div className="vs-maint-title">Your account is taken offline</div>
            <p className="vs-maint-msg">{practice} has been taken offline by VoiceSpectrum. Your data is safe.</p>
            <div className="vs-po-lists">
              <div>
                <div className="vs-po-h">What still works</div>
                <ul className="vs-po-list">
                  {w.works.map((x) => <li key={x} className="vs-po-ok"><span aria-hidden="true">✓</span>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="vs-po-h">What does not</div>
                <ul className="vs-po-list">
                  {w.not.map((x) => <li key={x} className="vs-po-no"><span aria-hidden="true">✕</span>{x}</li>)}
                </ul>
              </div>
            </div>
            <div className="vs-po-actions">
              <button type="button" className="vs-maint-btn" onClick={() => void check(true)} disabled={checking} data-testid="practice-offline-check">
                {checking ? 'Checking…' : 'Check status'}
              </button>
            </div>
            {checkMsg && <div className={`vs-po-check${checkMsg.err ? ' is-err' : ''}`} role="status">{checkMsg.text}</div>}
            <div className="vs-po-links">
              <ReportIssue kind="taken_offline" onOnline={() => void check(true)} />
              <button type="button" className="vs-maint-link" onClick={() => setContactOpen(true)} data-testid="practice-offline-contact">Contact support</button>
            </div>
            {info.since && <div className="vs-po-since">Offline since {new Date(info.since).toLocaleString([], { weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>}
          </>
        )}
      </div>
    </div>
  );
}
